import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";

// Halaman-halaman di bawah ini di-lazy-load (bukan di-import langsung di
// atas) supaya bundle awal yang diunduh browser cuma berisi Landing/login —
// modul besar seperti ForeaModule (~1900 baris) atau InventaModule (~1550
// baris) baru diunduh saat rute itu benar-benar dibuka, bukan di kunjungan
// pertama ke aplikasi apa pun halaman yang dibuka pengguna. Landing sengaja
// TETAP eager (bukan lazy) karena itu halaman pertama yang dilihat semua
// orang — melazy-load-nya justru menambah jeda yang tidak perlu.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const EntryRecord = lazy(() => import("@/pages/EntryRecord"));
const OperationRecord = lazy(() => import("@/pages/OperationRecord"));
const ReviewRecords = lazy(() => import("@/pages/ReviewRecords"));
const Settings = lazy(() => import("@/pages/Settings"));
const Profile = lazy(() => import("@/pages/Profile"));
const DokplotPage = lazy(() => import("@/pages/DokplotPage"));
const ForeaPage = lazy(() => import("@/pages/ForeaPage"));
const ForeaRiwayatPage = lazy(() => import("@/pages/ForeaRiwayatPage"));
const InventaPage = lazy(() => import("@/pages/InventaPage"));
const MonetaPage = lazy(() => import("@/pages/MonetaPage"));

function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

// Sama seperti fallback "Memuat..." yang sudah dipakai ProtectedRoute saat
// menunggu status auth — dipakai lagi di sini sebagai fallback Suspense saat
// menunggu chunk halaman selesai diunduh.
function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A1628] text-[#D4AF37] font-display text-xl">
      Memuat...
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" theme="dark" />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/entry" element={<ProtectedRoute><EntryRecord /></ProtectedRoute>} />
            <Route path="/record/:code/:id" element={<ProtectedRoute><OperationRecord /></ProtectedRoute>} />
            <Route path="/review" element={<ProtectedRoute><ReviewRecords /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute adminOnly><Settings /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/dokplot" element={<ProtectedRoute><DokplotPage /></ProtectedRoute>} />
            <Route path="/forea" element={<ProtectedRoute><ForeaPage /></ProtectedRoute>} />
            <Route path="/forea/riwayat" element={<ProtectedRoute><ForeaRiwayatPage /></ProtectedRoute>} />
            <Route path="/inventory" element={<ProtectedRoute><InventaPage /></ProtectedRoute>} />
            <Route path="/moneta" element={<ProtectedRoute><MonetaPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
